import { Tag, Link } from "@carbon/react";
import { toDate } from "@flaminem/utils";
import { FC } from "react";
import { generatePath, useNavigate } from "react-router-dom";

import WidgetRenderer from "src/dynamic-view-renderer/Widgets/WidgetRenderer.tsx";
import { useFormatDate, useT } from "src/internationalization";
import { ShortAlert } from "src/types/alert";
import { ScenarioExecutionResultWithCase } from "src/types/scenario";

import {
  BasicWidgetHorizontalProperty,
  BasicWidgetHorizontalPropertyLayout,
} from "../BasicWidget/BasicWidgetHorizontal";
import WidgetContainer, { WidgetContainerProps } from "../WidgetContainer";
import { DEFAULT_VALUE } from "../WidgetProperties/widgetProperties.constants";

export type ExecutionDetailWidgetProps = {
  data?: {
    executionResult?: ScenarioExecutionResultWithCase | null;
    selectedCase?: {
      id: string;
      request: string | null;
      alerts: ShortAlert[];
    };
  };
} & Omit<WidgetContainerProps, "children">;

export const ExecutionDetailWidget: FC<ExecutionDetailWidgetProps> = ({
  data,
  ...containerProps
}) => {
  const t = useT();
  const formatDate = useFormatDate();
  const navigate = useNavigate();
  const executionResult = data?.executionResult;

  if (!executionResult) {
    return null;
  }

  const properties = [
    {
      label: t("page.case.tab.executions.result.date"),
      value: formatDate(toDate(executionResult.date), {
        timeStyle: "short",
        dateStyle: "medium",
      }),
    },
    {
      label: t("page.case.tab.executions.result.scenario"),
      value: (
        <Link
          onClick={(e) => {
            e.preventDefault();
            navigate(
              generatePath("/scenario/:caseTypeCode/:id/active", {
                caseTypeCode: executionResult.scenarioVersion?.scenario?.caseTypeCode || null,
                id: executionResult.scenarioVersion?.scenario?.id || null,
              })
            );
          }}
          className="text-blue-600 hover:text-blue-800"
        >
          {executionResult.scenarioVersion?.scenario?.name}
        </Link>
      ),
    },
    {
      label: t("page.case.tab.executions.result.versionArchived"),
      value: (
        <Link
          onClick={(e) => {
            e.preventDefault();

            navigate(
              generatePath("/case/:caseTypeCode/:id", {
                caseTypeCode: executionResult.creationCase?.typeCode || null,
                id: executionResult.creationCase?.id || null,
              })
            );
          }}
          className="text-blue-600 hover:text-blue-800"
        >
          {executionResult.creationCase?.request}
        </Link>
      ),
    },
    {
      label: t("page.case.tab.executions.result.alert"),
      value: executionResult.alert?.id ? (
        <Link
          onClick={(e) => {
            e.preventDefault();
            navigate(`/alert/${executionResult.alert?.id}`);
          }}
        >{`${t("page.case.tab.executions.result.alert.value", {
          number: data.selectedCase?.request,
        })}`}</Link>
      ) : (
        DEFAULT_VALUE
      ),
    },
    {
      label: t("page.case.tab.executions.result.scoreGlobal"),
      value: (
        <Tag type="red" className="whitespace-nowrap">
          {executionResult.globalScore}
        </Tag>
      ),
    },
  ];
  return (
    <div className="flex flex-col gap-5 w-full">
      <WidgetContainer {...containerProps}>
        <BasicWidgetHorizontalPropertyLayout>
          {properties.map((property, index) => (
            <BasicWidgetHorizontalProperty
              key={index}
              label={property.label}
              value={property.value}
            />
          ))}
        </BasicWidgetHorizontalPropertyLayout>
      </WidgetContainer>
      <RuleWidget scenarioExecutionResult={executionResult} />
    </div>
  );
};

type RuleWidgetProps = {
  scenarioExecutionResult?: ScenarioExecutionResultWithCase | null;
};

const RuleWidget: FC<RuleWidgetProps> = ({ scenarioExecutionResult }) => {
  return (
    <WidgetRenderer
      data={{
        widgetsData: {
          rules: { scenarioExecutionResult },
        },
      }}
      rowConfiguration={{ widgets: [{ widgetCode: "rules" }] }}
      widgetConfig={{ widgetCode: "rules" }}
    />
  );
};
